// Adapter to convert external API listings to the app's Property type.
// Updated for Backend v2 (Feb 2026):
//   - Primary key is now `somethingPropertyId` ("SP-PROPERTY24-123456")
//   - `listingId` is now a number (external portal ID)
//   - `marketingHeading` replaces `title` for display
//   - `listPrice` replaces `price`
//   - `listingStatus` replaces `status` with full enum
//   - `agents` array is pre-populated with full agent details
//   - `images` can be string[] or Array<{ url, fileId, ... }>
//
// Backward compatible: handles both v1 and v2 response formats.

import type { Property, PropertyMedia } from '../types/property';
import type { ExternalListing, ExternalAgent } from '../types/external-listing';

// Map external propertyType strings to internal Property types
const PROPERTY_TYPE_MAP: Record<string, Property['type']> = {
  House: 'House',
  Townhouse: 'Townhouse',
  Apartment: 'Apartment',
  FlatApartment: 'Apartment',
  Farm: 'Farm',
  VacantLand: 'VacantLand',
  Land: 'Land',
  Commercial: 'Commercial',
  Industrial: 'Commercial',
};

/**
 * Try to extract suburb/city from a flat location string.
 * v1: location is a simple string like "20788 Uhlobo Lane".
 * v2: location is an object { latitude, longitude, address }.
 */
function parseLocation(listing: ExternalListing): Property['address'] {
  const loc = listing.location;

  // v2: location is an object with address string
  if (loc && typeof loc === 'object' && 'address' in loc) {
    return {
      street: loc.address || '',
      suburb: '',
      city: '',
      province: '',
      postalCode: '',
    };
  }

  // Legacy locationObj field (deprecated but kept for compat)
  if (listing.locationObj?.address) {
    return {
      street: listing.locationObj.address,
      suburb: '',
      city: '',
      province: '',
      postalCode: '',
    };
  }

  // v1: location is a plain string
  return {
    street: typeof loc === 'string' ? loc : '',
    suburb: '',
    city: '',
    province: '',
    postalCode: '',
  };
}

/**
 * Parse geo coordinates from v2 location object or legacy locationObj.
 */
function parseGeo(listing: ExternalListing): Property['geo'] {
  const loc = listing.location;
  // v2: location is an object with latitude/longitude
  if (loc && typeof loc === 'object' && 'latitude' in loc && 'longitude' in loc) {
    return {
      lat: loc.latitude,
      lng: loc.longitude,
    };
  }
  // Legacy locationObj field
  if (listing.locationObj?.latitude && listing.locationObj?.longitude) {
    return {
      lat: listing.locationObj.latitude,
      lng: listing.locationObj.longitude,
    };
  }
  return { lat: 0, lng: 0 };
}

/**
 * Extract a numeric feature value from the v2 features array.
 * e.g. getFeatureValue(listing.features, 'Bedrooms') → 3
 */
function getFeatureValue(features: ExternalListing['features'], type: string): number | undefined {
  if (!features) return undefined;
  const f = features.find(feat => feat.type.toLowerCase() === type.toLowerCase());
  return f?.value;
}

/**
 * Normalize images — v1 returns string[], v2 may return object[].
 */
function normalizeImages(listing: ExternalListing): PropertyMedia[] {
  const images = listing.images || [];
  const listingId = String(listing.somethingPropertyId || listing.listingId);

  return images.map((img, index) => {
    const url = typeof img === 'string' ? img : (img as any).url || '';
    const caption = typeof img === 'string' ? undefined : (img as any).caption;
    return {
      id: `img-${listingId}-${index}`,
      url,
      alt: caption || `${listing.marketingHeading || listing.title} - Image ${index + 1}`,
      isCover: index === 0,
      order: index,
    };
  });
}

/**
 * Build the best agent info from a v2 listing's `agents` array.
 * Returns a simplified agent object for the Property type.
 */
function buildAgentInfo(listing: ExternalListing): Property['agent'] | undefined {
  const agents = listing.agents;
  if (!agents || agents.length === 0) return undefined;

  const agent = agents[0];
  const name = `${agent.firstName || ''} ${agent.lastName || ''}`.trim();
  const phone = agent.phoneNumbers?.[0]
    || agent.telephoneNumbers?.[0]?.number
    || '';

  return {
    id: agent.agentId,
    name: name || agent.email?.split('@')[0] || 'Unknown',
    email: agent.email || agent.emailAddress || '',
    phone,
    whatsapp: phone, // Default to same phone
  };
}

/**
 * Optional agent mapping: pass a map of externalAgentId → internal agentId
 * so the adapter can auto-fill the agentId field during conversion.
 */
export type ExternalAgentMap = Record<string, string>;

/**
 * Get the canonical property ID from a listing.
 * Backend v2 uses `somethingPropertyId`, v1 uses `listingId`.
 */
function getCanonicalId(listing: ExternalListing): string {
  // Prefer somethingPropertyId (v2 primary key)
  if (listing.somethingPropertyId) {
    return listing.somethingPropertyId;
  }
  // Fallback to listingId (v1)
  return String(listing.listingId);
}

/**
 * Convert an ExternalListing from the AWS API into the app's Property shape.
 *
 * Backend v2: Uses `somethingPropertyId` as the canonical ID when available.
 * The `externalId` stores the portal's `listingId` for dedup.
 *
 * @param listing - The external listing from the API
 * @param agentMap - Optional map of externalAgentId → internal agentId for auto-assignment
 */
export function externalListingToProperty(listing: ExternalListing, agentMap?: ExternalAgentMap): Property {
  const media = normalizeImages(listing);
  const canonicalId = getCanonicalId(listing);
  const listingIdStr = String(listing.listingId);

  // Timestamps: v2.1 uses `created` (ISO string), v1 uses `createdAt` (Unix ms)
  let createdAtISO: string;
  if (listing.created) {
    createdAtISO = listing.created;
  } else {
    const createdAtMs = typeof listing.createdAt === 'number'
      ? listing.createdAt
      : Number(listing.createdAt) || Date.now();
    createdAtISO = new Date(createdAtMs).toISOString();
  }
  const createdAtMs = typeof listing.createdAt === 'number'
    ? listing.createdAt
    : (listing.created ? new Date(listing.created).getTime() : Date.now());

  // Build the external agent link if agents are provided
  // v2.1: prefer compositeAgentIds, then agents[].agentId, then agentIds[]
  const externalAgentId = listing.compositeAgentIds?.[0]
    || listing.agents?.[0]?.agentId
    || (listing.agentIds?.[0] ? String(listing.agentIds[0]) : undefined);

  // Use v2 fields with v1 fallbacks
  const title = listing.marketingHeading || listing.title || 'Untitled Property';
  const description = listing.marketingDescription || listing.description || '';
  const price = listing.listPrice ?? listing.price ?? 0;

  return {
    id: canonicalId,
    slug: canonicalId,
    title,
    type: PROPERTY_TYPE_MAP[listing.propertyType] || 'House',
    status: 'Published', // External listings are treated as published
    // Backend v2 primary key
    somethingPropertyId: listing.somethingPropertyId,
    // v2 listing status
    listingStatus: listing.listingStatus,
    // Canonical external ID — the portal's listing ID
    externalId: listingIdStr,
    externalSource: listing.source || 'property24',
    address: parseLocation(listing),
    geo: parseGeo(listing),
    price,
    facts: {
      bedrooms: listing.bedrooms || getFeatureValue(listing.features, 'Bedroom') || getFeatureValue(listing.features, 'Bedrooms') || undefined,
      bathrooms: listing.bathrooms || getFeatureValue(listing.features, 'Bathroom') || getFeatureValue(listing.features, 'Bathrooms') || undefined,
      parking: listing.parkingSpaces || getFeatureValue(listing.features, 'Garage') || getFeatureValue(listing.features, 'Parking') || undefined,
      floorSize: listing.floorArea?.size || listing.area || undefined,
      erfSize: listing.erfSize?.size || undefined,
    },
    amenities: [],
    media,
    description,
    listingDate: createdAtISO,
    // Auto-resolve internal agentId if an agent mapping was provided
    agentId: (externalAgentId && agentMap?.[externalAgentId]) ? agentMap[externalAgentId] : '',
    // Populate agent details from v2 agents array
    agent: buildAgentInfo(listing),
    externalAgentId,
    visibility: 'Public',
    flags: {
      new: Date.now() - createdAtMs < 7 * 24 * 60 * 60 * 1000, // < 7 days old
    },
    // v2.1 badge fields
    noTransferDuty: listing.noTransferDuty,
    priceReduced: listing.priceReduced,
    // v2.1 financial details
    rates: listing.rates,
    levy: listing.levy,
    // v2.1 mandate and features
    mandateType: listing.mandateType,
    features: listing.features,
    // v2.1 pre-formatted composite agent IDs
    compositeAgentIds: listing.compositeAgentIds,
    // Metadata for external listings
    _external: {
      source: listing.source || 'property24',
      listingId: listingIdStr,
      url: listing.url,
      lastSynced: listing.lastSynced
        ? new Date(listing.lastSynced).toISOString()
        : new Date().toISOString(),
    },
  };
}

/**
 * Convert a batch of external listings to Property[].
 *
 * @param listings - Array of external listings
 * @param agentMap - Optional map of externalAgentId → internal agentId for auto-assignment
 */
export function externalListingsToProperties(listings: ExternalListing[], agentMap?: ExternalAgentMap): Property[] {
  return listings.map(l => externalListingToProperty(l, agentMap));
}

/**
 * Check if a Property originated from the external API.
 * Works with somethingPropertyId (v2), externalId, and old `ext-` prefixed IDs.
 */
export function isExternalProperty(property: Property): boolean {
  return !!(
    property.somethingPropertyId ||
    property.externalId ||
    property._external?.listingId ||
    property.id.startsWith('ext-') ||
    property.id.startsWith('SP-')
  );
}

/**
 * Get the original portal URL for an external property.
 */
export function getExternalPropertyUrl(property: Property): string | null {
  return property._external?.url || null;
}

/**
 * Get the source portal name for an external property.
 */
export function getExternalPropertySource(property: Property): string | null {
  return property.externalSource || property._external?.source || null;
}

/**
 * Get the somethingPropertyId for API v2 calls.
 * Falls back to constructing one from source + listingId.
 */
export function getSomethingPropertyId(property: Property): string | null {
  if (property.somethingPropertyId) return property.somethingPropertyId;
  // Try to construct from available data
  const source = property.externalSource || property._external?.source;
  const listingId = property.externalId || property._external?.listingId;
  if (source && listingId) {
    return `SP-${source.toUpperCase()}-${listingId}`;
  }
  return null;
}